import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, logout } from '../lib/adminAPI';
import { ArrowLeft, Mail, Phone, Award, MapPin, Calendar, Globe } from 'lucide-react';
import { toast } from 'sonner';

const AgentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgent();
  }, [id]);

  const loadAgent = async () => {
    try {
      const data = await adminAPI.getAgentById(id);
      setAgent(data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading agent details...</div>;
  }

  if (!agent) {
    return <div className="admin-error">Agent not found</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <button onClick={() => navigate('/admin/agents')} className="admin-back-btn">
            <ArrowLeft size={20} />
            Back to Agents
          </button>
          <h1 className="admin-page-title">Agent Application Details</h1>
        </div>
        <button onClick={logout} className="admin-logout-btn">
          Logout
        </button>
      </div>

      <div className="admin-detail-container">
        <div className="admin-detail-card">
          <h2 className="admin-detail-section-title">Agent Information</h2>
          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <label>Agent Name</label>
              <p>{agent.agentName}</p>
            </div>
            <div className="admin-detail-item">
              <label><Mail size={16} /> Email</label>
              <p><a href={`mailto:${agent.email}`}>{agent.email}</a></p>
            </div>
            <div className="admin-detail-item">
              <label><Phone size={16} /> Phone</label>
              <p><a href={`tel:${agent.phone}`}>{agent.phone}</a></p>
            </div>
            <div className="admin-detail-item">
              <label><Calendar size={16} /> Submitted</label>
              <p>{new Date(agent.submittedAt).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="admin-detail-card">
          <h2 className="admin-detail-section-title">Professional Details</h2>
          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <label><Award size={16} /> License Number</label>
              <p>{agent.licenseNumber}</p>
            </div>
            <div className="admin-detail-item">
              <label>Years of Experience</label>
              <p>{agent.yearsExperience}</p>
            </div>
            <div className="admin-detail-item">
              <label>Agency Name</label>
              <p>{agent.agencyName || 'Not specified'}</p>
            </div>
            <div className="admin-detail-item">
              <label><Globe size={16} /> Website</label>
              <p>
                {agent.website ? (
                  <a href={agent.website} target="_blank" rel="noopener noreferrer">
                    {agent.website}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="admin-detail-card">
          <h2 className="admin-detail-section-title">Service Areas</h2>
          <div className="admin-detail-item">
            <label><MapPin size={16} /> Coverage</label>
            <p>{agent.serviceAreas}</p>
          </div>
        </div>

        {agent.message && (
          <div className="admin-detail-card">
            <h2 className="admin-detail-section-title">About / Message</h2>
            <p className="admin-detail-message">{agent.message}</p>
          </div>
        )}

        {agent.recaptchaScore && (
          <div className="admin-detail-card">
            <h2 className="admin-detail-section-title">Security</h2>
            <div className="admin-detail-item">
              <label>reCAPTCHA Score</label>
              <p>
                <span className={`admin-score-badge ${agent.recaptchaScore >= 0.7 ? 'score-high' : agent.recaptchaScore >= 0.5 ? 'score-medium' : 'score-low'}`}>
                  {agent.recaptchaScore.toFixed(2)}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDetail;
